(ns common.ui.list
  (:require ["@dnd-kit/core" :as dnd-core]
            ["@dnd-kit/sortable" :as dnd-sortable]
            ["@dnd-kit/utilities" :as dnd-utilities]
            ["@mui/material/Box$default" :as Box]
            ["@mui/material/Button$default" :as Button]
            ["@mui/material/Icon$default" :as Icon]
            ["@mui/material/IconButton$default" :as IconButton]
            ["@mui/material/Link$default" :as Link]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/Typography$default" :as Typography]
            [cljs-bean.core :refer [->js]]
            [clojure.spec.alpha :as spec]
            [common.datascript.sequence :as dbseq]
            [common.ui.entity :as ui.entity]
            [common.ui.store :as store]
            [common.url :as url]
            [common.url-template :as url-tpl]
            [common.util :refer [spread-props]]
            [common.widgets.layout :refer [stack-divider]]
            [mate.core :as mc]
            [re-frame.core :as rf]
            [uix.core :as uix :refer [$ defui]]
            [uix.re-frame :refer [use-subscribe]]))

(spec/def :list/items
  (spec/or :subscription :reframe/sub-or-event
           :attribute keyword?))

(spec/def :list/sort-by ifn?)

(spec/def :list/reorderable? boolean?)
(spec/def :list/common-item-details ifn?)
(spec/def :list/content-before ifn?)
(spec/def :list/content-after ifn?)
(spec/def :list/->item-cfg ifn?)
(spec/def :list/->item-db-entity fn?)
(spec/def :list/empty-msg :react/element)
(spec/def :list/allow-delete-items? boolean?)
(spec/def :list/item-labels-editable? boolean?)
(spec/def :list/highlighted boolean?)

(defn reorderable? [{:entity/keys [read-only? ref] :list/keys [reorderable? items sort-by]}]
  (and reorderable? (not read-only?) (keyword? items) (keyword? sort-by) (some? ref)))

(defn list-type [cfg]
  (cond
    (reorderable? cfg) :sequence-relation
    (and (vector? (:list/items cfg)) (nil? (:entity/ref cfg))) :collection
    (contains? cfg :list/items) :unordered-relation))

(defmulti list-spec list-type)

(defmethod list-spec :sequence-relation [_]
  (spec/keys :req [:entity/ref :list/items :list/sort-by :list/reorderable?]))

(defmethod list-spec :unordered-relation [_]
  (spec/merge
   (spec/keys :req [:list/items]
              :opt [:list/sort-by])
   (spec/or :reframe (spec/and (spec/keys :req [:list/items])
                               #(= :subscription (first (:list/items %))))
            :datascript (spec/and (spec/keys :req [:entity/ref :list/items])
                                  #(= :attribute (first (:list/items %)))))))

(spec/def :collection-list/delete-item-event keyword?)

(defmethod list-spec :collection [_]
  (spec/keys :req [:list/items]
             :opt [:collection-list/delete-item-event]))

(spec/def :list/configuration
  (spec/merge (spec/keys :req [:list/->item-cfg]
                         :opt [:entity/read-only?
                               :list/allow-delete-items?
                               :list/item-labels-editable?
                               :list/common-item-details
                               :list/content-before
                               :list/content-after
                               :list/empty-msg
                               :list/->item-db-entity
                               :list/highlighted])
              (spec/multi-spec list-spec :list/type)))

(spec/def :list-item/details :react/element)

(spec/def :list-item/allow-delete-item?
  (spec/or :subscription :reframe/sub-or-event
           :boolean boolean?))

(spec/def :list-item/configuration
  (spec/merge :entity/configuration
              (spec/keys :opt [:list-item/details])))

(defn delete-item [{:list/keys [delete-item-event] :as cfg} {e :eid :as ref}]
  (cond
    (instance? dbseq/SequenceRelation cfg)
    (dbseq/delete cfg e)

    (some? delete-item-event)
    (rf/dispatch [delete-item-event ref])

    (instance? store/DataScriptDBEntity ref)
    (rf/dispatch [:ui.store/delete e])))

(defui list-sequence-element-drag-handle [{:keys [listeners attributes]}]
  ($ Icon (spread-props
           #js {:sx {:color "grey.50"
                     :cursor "grab"}}
           attributes
           listeners)
     "drag_indicator"))

(defmulti items list-type)

(defmethod items :unordered-relation
  [{{container :eid} :entity/ref relation :list/items sort-by-fn :list/sort-by}]
  (let [sort-vector (cond (nil? sort-by-fn)
                          #(vector % %)
                          (keyword? sort-by-fn)
                          (fn [e] [@(store/get-value (store/->DataScriptDBEntity e) sort-by-fn) e])
                          :else
                          (fn [e] [(sort-by-fn e) e]))]
    (->> @(rf/subscribe (cond (keyword? relation)
                              [:ui.store/values container relation]
                              (vector? relation)
                              relation))
         (mapv sort-vector)
         (sort-by first))))

(defmethod items :sequence-relation
  [sr]
  (dbseq/ordered sr))

(defmethod items :collection
  [{items-sub :list/items}]
  (mc/seq-indexed @(rf/subscribe items-sub)))

(defn sequence-list [cfg]
  (if (reorderable? cfg)
    (dbseq/map->SequenceRelation
     (assoc cfg
            :position-attribute (:list/sort-by cfg)
            :sequence-relation (:list/items cfg)
            :container (get-in cfg [:entity/ref :eid])))
    cfg))

(defui empty-msg
  "component for showing empty message"
  [{:keys [title children]}]
  ($ Stack {:gap 1}
     (when (some? title)
       ($ Typography {:variant "h5"} title))
     children))

(defui empty-msg-link
  [{:keys [menu children] :as props}]
  ($ Link
     (-> (dissoc props :menu)
         (assoc :href (use-subscribe [:side-menu/link menu])))
     children))

(defui empty-msg-button
  [{:keys [variant children]
    :or {variant "text"} :as props}]
  ($ Button (merge props
                   {:variant variant
                    :sx #js {:verticalAlign "baseline"
                             :padding 0}})
     children))

(defn get-location []
  (-> (.getQuery (url/current-uri)) url/query-string->map))

(defn- view-item
  [{:list/keys [allow-delete-items? item-labels-editable? common-item-details]
    read-only-list? :entity/read-only?
    highlighted? :list/highlighted
    :or {item-labels-editable? true
         allow-delete-items? true
         highlighted? false}
    :as list-cfg}
   {:entity/keys [ref icon label read-only?]
    :list-item/keys [details allow-delete-item? hide-divider? drag-listeners drag-attributes]
    :as item-cfg}]
  {:pre [(spec/assert :list-item/configuration item-cfg)]}
  (let [allow-delete-item? (if (some? allow-delete-item?) allow-delete-item? (not read-only?))
        active @(rf/subscribe [:entity/current])
        icon* (if (sequential? icon) @(rf/subscribe icon) icon)
        editable-label? (and (not read-only?) (not read-only-list?)
                             (keyword? label)
                             ref item-labels-editable?)
        allow-delete-item? (if (vector? allow-delete-item?)
                             @(rf/subscribe allow-delete-item?)
                             allow-delete-item?)
        ;; handling here special case for rule selection
        ;; need to get value from uri cause url-context contains wrong value
        is-rule? (= (:entity/label item-cfg) :rule/name)
        rule-name (when is-rule? (:name (get-location)))
        selected? (if rule-name
                    (= rule-name (str @(store/get-value ref label)))
                    (= ref (:entity/ref active)))
        query-params (:entity/query-param item-cfg)

        label-style (cond-> {:font-weight "600"
                             "&:hover" {:color "primary.main"}}
                      selected? (assoc :color "text.selected"))]
    [:> Stack {:component "li"
               :role "listitem"
               :data-testid "list-item"
               :sx {:border-radius "0.25rem"
                    :background-color (if (and highlighted? selected?) "primary.100" "grey.90")}
               :divider (when-not hide-divider? ($ stack-divider))}
     ;; header
     [:> Stack {:direction "row"
                :align-items "center"
                :spacing 1
                :padding 2
                :data-component :list-item__header}

      (when (and (reorderable? list-cfg) drag-listeners drag-attributes)
        ($ list-sequence-element-drag-handle {:listeners drag-listeners
                                              :attributes drag-attributes}))

      (when (some? icon*)
        [:> Icon {:data-testid "list-item-icon"
                  :font-size "small"} icon*])

      (if editable-label?
        [ui.entity/editable-label {:input-props {:data-testid "list-item-input"}
                                   :ref ref
                                   :label label}]
        (let [label (if (keyword? label) (str @(store/get-value ref label)) label)]
          [(if (string? label) :p :div)
           {:style {:flex-grow 1}
            :data-component :list-item-label-readonly
            :data-label-type :readonly
            :data-testid (str "list-item-label-" (when (string? label) label))}
           (if query-params
             [:> Link
              {:href @(rf/subscribe [:entity/link @(rf/subscribe query-params)])
               :underline "none"
               :color "inherit"
               :sx label-style}
              label]
             label)]))

      (when (and (not read-only-list?)
                 allow-delete-items?
                 allow-delete-item? ref)
        [:> IconButton {:data-testid :delete-list-item
                        :on-click #(delete-item list-cfg ref)}
         [:> Icon "delete"]])]

     (if (contains? #{"urn:lemonpi:schema:context:minsight-nl:v1"} @(store/get-value ref :context-source/schema))
       [:> Stack {:direction "row"
                  :align-items "center"
                  :px 2
                  :py 1}
        [:> Icon {:color "warning"
                  :sx {:mr 1.5}} "warning"]
        [:> Typography {}
         "Since December 1st 2022, “mInsights” is no longer available as a data source."]]
       (when (some? details)
         [:> Box {:px 2 :py 1
                  :data-component :list-item__details
                  :data-testid "list-item-details"} details]))

     (when (some? common-item-details)
       ;; The common-item-details function is responsible to set correct
       ;; padding/margin, e.g. {:px 2 :py 1}
       [common-item-details list-cfg item-cfg])]))

(defn list-items-container
  [{:list/keys [sx] :or {sx {:gap 1 :p 3 :m 0 :overflowY "auto"}}} & items]
  (into [:> Stack {:component "ul" :sx sx :role "list"}] items))

(defn list-sequence-element [{:keys [id list-cfg item-cfg]}]
  (let [sortable (dnd-sortable/useSortable #js {:id id})
        set-ref (.-setNodeRef sortable)
        attrs (.-attributes sortable)
        listeners (.-listeners sortable)
        transform (.-transform sortable)
        transition (.-transition sortable)
        is-dragging (.-isDragging sortable)]
    [:div {:ref set-ref
           :style {:transform (dnd-utilities/CSS.Transform.toString transform)
                   :transition transition
                   :opacity (if is-dragging 0.8 1)}}
     [view-item list-cfg
      (assoc item-cfg
             :list-item/drag-listeners listeners
             :list-item/drag-attributes attrs)]]))

(defn build-list-sequence [{:keys [items list-cfg]}]
  (let [item-ids (mapv #(store/identifier (:entity/ref %)) items)

        sensors (dnd-core/useSensors
                 (dnd-core/useSensor dnd-core/PointerSensor)
                 (dnd-core/useSensor dnd-core/KeyboardSensor
                                     #js {:coordinateGetter dnd-sortable/sortableKeyboardCoordinates}))

        handle-drag-end (uix/use-callback
                         (fn [^js event]
                           (let [active-id (-> event .-active .-id)
                                 over-id (-> event .-over .-id)]
                             (when (not= active-id over-id)
                               (let [old-index (.indexOf item-ids active-id)
                                     new-index (.indexOf item-ids over-id)]
                                 (dbseq/change-position list-cfg (inc old-index) (inc new-index))))))
                         [item-ids list-cfg])]

    [:> dnd-core/DndContext
     {:sensors sensors
      :collisionDetection dnd-core/closestCenter
      :onDragEnd handle-drag-end}
     [:> dnd-sortable/SortableContext
      {:items (->js item-ids)
       :strategy dnd-sortable/verticalListSortingStrategy}
      [list-items-container list-cfg
       (for [id item-ids]
         (let [item-cfg (first (filter #(= (store/identifier (:entity/ref %)) id) items))]
           ^{:key id}
           [:f> list-sequence-element
            {:id id
             :list-cfg list-cfg
             :item-cfg item-cfg}]))]]]))

(defn view-items [cfg items-cfgs]
  (if (reorderable? cfg)
    [:f> build-list-sequence
     {:items items-cfgs
      :list-cfg cfg}]
    [list-items-container cfg
     (doall (for [{e :entity/ref :as item-cfg} items-cfgs]
              ^{:key (store/identifier e)} [view-item cfg item-cfg]))]))

(defn view [{:list/keys [->item-cfg ->item-db-entity content-before content-after empty-msg]
             :or {->item-db-entity store/->DataScriptDBEntity} :as cfg}]
  {:pre [(spec/assert :list/configuration cfg)]}
  (let [cfg (sequence-list cfg)
        items-cfgs (map (comp ->item-cfg ->item-db-entity second) (items cfg))]
    [:> Stack {:divider ($ stack-divider)
               :data-component :list
               :sx #js {:overflow "hidden"}}
     (when content-before [content-before cfg items-cfgs])
     (if (and empty-msg (empty? items-cfgs))
       [:> Box {:my 1
                :p 3
                :max-width "70rem"
                :data-testid :empty-msg} empty-msg]
       [view-items cfg items-cfgs])
     (when content-after [content-after cfg items-cfgs])]))

(defn concatenate
  "render multiple list views below each other such that it visually appears as
  one single list"
  [list-view-fn & list-cfgs]
  (into
   [:> Stack {:spacing 1 :p 3}]
   (for [cfg list-cfgs]
     [list-view-fn (assoc cfg :list/sx {:gap 1 :p 0 :m 0})])))
